/**
 * Shared helpers for the StatefulMacroCoordinator.
 *
 * Kept out of the coordinator file so the coordinator stays focused on the
 * walk algorithm and stays within the project's file-size budget.
 */

import type { MacroMessage, MacroWalkResponsePayload } from '@playcastdotio/Messaging';

/**
 * Narrow a discriminated MacroMessage to its response payload portion.
 *
 * `MacroMessage` is `discriminatedUnion('type', [request, response])` — both
 * variants carry the base envelope plus a flat payload. Once we've matched
 * `type === MACRO_WALK_RESPONSE`, the message IS the payload (with extra
 * envelope fields). Cast through unknown for a focused, named view.
 */
export function extractWalkResponsePayload(message: MacroMessage): MacroWalkResponsePayload {
  return message as unknown as MacroWalkResponsePayload;
}

/**
 * Generate a fresh launch instance id used to scope the walk to a single
 * launch attempt. The host owns this id end-to-end — the API simply echoes
 * it back so we can drop late/duplicated responses.
 *
 * NOTE — Phase 3 deviation from PHASE_3.md pseudocode: the plan wrote
 * `launchInstanceId = pavilionCoordinator.currentLaunchInstanceId`, but
 * `PavilionCoordinator.currentLaunchInstanceId` is only set inside
 * `handleGameLaunched()` (which fires from the `gameReady` event). For
 * stateful campaigns, `gameReady` does NOT fire until AFTER the walk
 * completes — so reading from PavilionCoordinator at walk-start time would
 * always observe `null` or stale. The cleanest fix that honors the
 * "host-owned identifier for the current launch attempt" definition is to
 * generate it here. PavilionCoordinator's getter (`getCurrentLaunchInstanceId()`)
 * still exists for future consumers.
 */
export function generateLaunchInstanceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `walk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export type WaitCaptureErrorAction = 'retry' | 'restart' | 'legacy';

/**
 * Classify Playjector macro-step failures while polling a Wait node.
 * Non-Wait captures intentionally retain the coordinator's legacy fallback.
 */
export function classifyWaitCaptureError(error: unknown): WaitCaptureErrorAction {
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();

  if (message.includes('no process') && !message.includes('process not ready')) {
    return 'restart';
  }

  if (
    message.includes('process not ready')
    || message.includes('no hashes')
    || message.includes('already in flight')
    || message.includes('abandoned')
    || (message.includes('macro step reply timed out') && message.includes('10000ms'))
  ) {
    return 'retry';
  }

  return 'legacy';
}
